import type { InternalAxiosRequestConfig } from 'axios';
import { isAxiosError } from 'axios';

export type AxiosConfigWithDiag = InternalAxiosRequestConfig & {
  __diagPayloadApproxBytes?: number;
  __diagPayloadKind?: string;
};

const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max)}… [+${value.length - max} chars]`;

export function attachApproxRequestPayloadBytes(config: AxiosConfigWithDiag): void {
  const data = config.data;
  if (data instanceof FormData) {
    config.__diagPayloadKind = 'multipart/form-data';
    return;
  }
  if (!data) return;
  try {
    const serialized = typeof data === 'string' ? data : JSON.stringify(data);
    config.__diagPayloadApproxBytes = new TextEncoder().encode(serialized).length;
  } catch {
    // ignore
  }
}

function responseBodyPreview(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === 'string') return truncate(data, 600);
  try {
    return truncate(JSON.stringify(data), 600);
  } catch {
    return String(data);
  }
}

/**
 * Log sempre visível no DevTools (mesmo em bundle de produção), para depurar 413/502 etc.
 * Não inclui token nem corpo completo da resposta.
 */
export function logAxiosErrorDiagnostics(context: string, error: unknown): void {
  if (!isAxiosError(error)) {
    console.warn(`[API DIAG] ${context}`, { kind: 'non-axios', error });
    return;
  }

  const cfg = error.config as AxiosConfigWithDiag | undefined;
  const relative = cfg?.url || '';
  const fullUrl =
    cfg?.baseURL != null && cfg?.url != null ? `${String(cfg.baseURL).replace(/\/$/, '')}${relative}` : relative;
  const method = (cfg?.method || 'get').toUpperCase();
  const res = error.response;
  const bytes = cfg?.__diagPayloadApproxBytes;
  const payloadKind = cfg?.__diagPayloadKind;

  console.warn(`[API DIAG] ${context}`, {
    method,
    url: fullUrl || relative,
    status: res?.status,
    statusText: res?.statusText,
    serverHeader: res?.headers?.server,
    contentType: res?.headers?.['content-type'],
    requestPayloadKind: payloadKind,
    requestPayloadApproxBytes: bytes,
    requestPayloadApproxMB: bytes != null ? (bytes / (1024 * 1024)).toFixed(3) : undefined,
    responseBodyPreview: res ? responseBodyPreview(res.data) : null,
    hint:
      res?.status === 413
        ? payloadKind === 'multipart/form-data'
          ? '413 em multipart: limite de body no proxy (client_max_body_size) ou middleware de upload.'
          : '413 em JSON: client_max_body_size no Nginx ou limit do Express; HTML no preview indica Nginx antes do Node.'
        : undefined,
  });
}
